import { Button } from '@shared/components/shadcn';
import { ChevronRight, Close, Info, Success, Warning } from '@shared/icons';
import { twMerge } from '@shared/themes';
import type { ComponentType, KeyboardEvent } from 'react';
import { Link } from '../Link/Link';
import type { StatusNotificationProps, StatusNotificationType, StatusNotificationVariant } from './types';

type TypeOption = {
  iconBg: string;
  icon: ComponentType<{ size: 'extra-small' }>;
  fixedAccent: string;
  floatingAccent: string;
};

const TYPE_OPTIONS = {
  info: {
    iconBg: 'bg-info-background-secondary text-foreground-inverse',
    icon: Info,
    fixedAccent: 'bg-info-background before:bg-info-foreground',
    floatingAccent: '[background:linear-gradient(hsl(var(--alpha-10)),hsl(var(--alpha-10))),hsl(var(--accent-blue))]',
  },
  warning: {
    iconBg: 'bg-warning-background-secondary text-foreground-inverse',
    icon: Warning,
    fixedAccent: 'bg-warning-background before:bg-warning-foreground',
    floatingAccent: '[background:linear-gradient(hsl(var(--alpha-10)),hsl(var(--alpha-10))),hsl(var(--accent-orange))]',
  },
  error: {
    iconBg: 'bg-notification-red text-foreground-inverse',
    icon: Close,
    fixedAccent: 'bg-error-background before:bg-error-foreground',
    floatingAccent: '[background:linear-gradient(hsl(var(--alpha-10)),hsl(var(--alpha-10))),hsl(var(--accent-red))]',
  },
  success: {
    iconBg: 'bg-button-background-accent text-foreground',
    icon: Success,
    fixedAccent: 'bg-success-background before:bg-success-foreground',
    floatingAccent: '[background:linear-gradient(hsl(var(--alpha-10)),hsl(var(--alpha-10))),hsl(var(--accent-green))]',
  },
} satisfies Record<StatusNotificationType, TypeOption>;

const CONTAINER_STYLES = {
  floating: 'p-4 rounded-xl border border-border',
  fixed: 'py-4 pr-4 pl-5 rounded-sm before:absolute before:left-0 before:top-0 before:h-full before:w-1',
} satisfies Record<StatusNotificationVariant, string>;

function StatusNotification({
  type,
  variant = 'fixed',
  actions = [],
  label,
  description,
  buttonText,
  buttonTestId,
  buttonTrackName,
  onClick,
  className,
  showChevron = false,
  dataTrackType,
  dataTrackMessage,
  dataTrackPeriod,
  role,
  ...props
}: StatusNotificationProps) {
  const option = TYPE_OPTIONS[type];
  const alertRole = role ?? (type === 'warning' || type === 'error' ? 'alert' : undefined);
  const isInteractiveChevron = Boolean(showChevron && onClick);

  const containerStyle = twMerge(
    CONTAINER_STYLES[variant],
    variant === 'fixed' ? option.fixedAccent : option.floatingAccent,
    'relative flex gap-4 overflow-hidden',
    showChevron ? 'flex-row items-center justify-between' : 'flex-col @md:flex-row @md:items-center @md:justify-between'
  );

  const rightSideContent = showChevron ? (
    <ChevronRight size="small" />
  ) : buttonText ? (
    <Button
      variant="primary"
      size="s"
      onClick={() => onClick?.()}
      {...(buttonTestId && { 'data-testid': buttonTestId })}
      {...(buttonTrackName && { 'data-track-name': buttonTrackName })}
    >
      {buttonText}
    </Button>
  ) : null;

  const handleChevronKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onClick?.();
  };

  const interactiveAriaLabel = label ?? (typeof description === 'string' ? description : undefined);

  return (
    <div
      className={twMerge(
        '@container',
        isInteractiveChevron &&
          'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className
      )}
      data-testid="status-notification"
      data-track-container="Dialog"
      {...(alertRole && { role: alertRole })}
      {...(dataTrackType && { 'data-track-type': dataTrackType })}
      {...(dataTrackMessage && { 'data-track-message': dataTrackMessage })}
      {...(dataTrackPeriod && { 'data-track-period': dataTrackPeriod })}
      {...props}
      {...(isInteractiveChevron && {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: handleChevronKeyDown,
        'aria-label': interactiveAriaLabel,
      })}
    >
      <div className={containerStyle}>
        <div className="flex gap-3">
          {variant === 'floating' ? (
            <div className={twMerge('light flex size-7 shrink-0 items-center justify-center rounded-md', option.iconBg)}>
              <option.icon size="extra-small" />
            </div>
          ) : null}
          <div className={twMerge('flex flex-col', variant === 'fixed' && showChevron && 'min-w-0 flex-1')}>
            {label && <h4 className="paragraph-strong">{label}</h4>}
            {description && <p className="mt-1 whitespace-pre-line paragraph-explanatory">{description}</p>}
            {variant === 'floating' && actions.length > 0 && (
              <div className="mt-2 flex justify-end gap-4" data-track-container="Actions">
                {actions.map(action => (
                  <Link
                    key={action.label}
                    href=""
                    onClick={action.onClick}
                    {...(action['data-track-name'] && {
                      'data-track-name': action['data-track-name'],
                    })}
                  >
                    {action.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        {rightSideContent && (
          <div
            className={twMerge('flex justify-end', showChevron ? 'shrink-0' : '@md:flex-basis-10')}
            {...(isInteractiveChevron && { 'aria-hidden': true })}
            {...(!isInteractiveChevron &&
              showChevron && {
                'data-testid': 'status-notification-chevron',
              })}
          >
            {rightSideContent}
          </div>
        )}
      </div>
    </div>
  );
}

export { StatusNotification };
